// What the 3D view builds again: after an edit, and while a work in 3D moves
// something — the objects that changed and what reads them. The scene's build
// (three/scene3d.ts `buildScene`) reads across objects; every such read is
// declared ONCE here, per kind of scene object (`SCENE_READS`, a Record over the
// kind: a new kind does not compile until it says what its build reads), and
// every rebuild asks the one function `sceneFollows` — the refresh after an
// edit (`sceneChanges`), a handle's or the gizmo's live step, a 3D drag's, a
// live Move (ui/view3d.ts `rebuildObjects3d`). The build record (`SceneBuilt`)
// is made and brought up to date here alone (`sceneRecord`, `markRebuilt`).
// - The whole scene: the first build, or its `whole` key moved (the underlays,
//   `SCENE_WHOLE`, built outside every object; the catalogue; the hidden layers
//   and disciplines), or an object has no version of its own (an array whose
//   ids are not unique is tracked whole).
// - An object that came, went or changed is built again, with what reads it.
// DOM-free (test/sceneChanges.test.mjs, which also builds the scene in node).
import type {
  Drawing,
  ProductInstance,
} from '../shared/types';
import { HEAVY_FIELDS } from './plainData';
import { inWallParts } from './wallCuts';

export type SceneKind =
  | 'conn'
  | 'part'
  | 'polygon'
  | 'room'
  | 'wall';

/** The drawing's array each kind's objects are in (each object's version is
 *  kept by its id there). */
export const SCENE_ARRAYS: Record<SceneKind, 'connections' | 'instances' | 'polygons' | 'walls'> = {
  conn: 'connections',
  part: 'instances',
  polygon: 'polygons',
  room: 'instances',
  wall: 'walls',
};

/** An object of the scene as it is now. */
export interface SceneObject {
  readonly id: string;
  readonly kind: SceneKind;
  readonly data: object;
}

/** The scene's objects in a drawing: its rooms and parts, drawn walls, shapes
 *  and connections. */
export function sceneObjects(d: Drawing): SceneObject[] {
  return [
    ...d.instances.map((i): SceneObject => ({ id: i.id, kind: i.autoBenches ? 'room' : 'part', data: i })),
    ...(d.walls ?? []).map((w): SceneObject => ({ id: w.id, kind: 'wall', data: w })),
    ...(d.polygons ?? []).map((p): SceneObject => ({ id: p.id, kind: 'polygon', data: p })),
    ...d.connections.map((c): SceneObject => ({ id: c.id, kind: 'conn', data: c })),
  ];
}

/** A role a part plays in other objects' builds, wherever it stands. */
export type PartRole =
  | 'inWall'
  | 'risers';

/** Who plays each role: a Record, so a new role says who plays it. */
const PART_ROLES: Record<PartRole, (p: ProductInstance) => boolean> = {
  // Set into a wall (a control unit's display), it cuts the rooms' walls (a hole
  // among their openings) and the drawn walls (a pocket): wallCuts.ts.
  inWall: (p) => inWallParts([p]).length > 0,
  // A shower mixer's hot and cold risers are built with the room whose wall it
  // is set into (risers.ts).
  risers: (p) => !!p.wallRisers,
};

/** The drawing's fields some kind's build reads. */
export type SceneField =
  | 'floorings'
  | 'lightBeams'
  | 'pipeTypes'
  | 'texts';

/** What a kind's build reads besides the object itself. */
export interface KindReads {
  /** The drawing's fields: one moved, and every object of the kind is built again. */
  readonly fields: readonly SceneField[];
  /** The parts in these roles, wherever they stand: one changed, came or went
   *  (or left its role), and every object of the kind is built again. */
  readonly roles: readonly PartRole[];
  /** The other objects: those its data names (`named`: a fibre kit its
   *  projector, a part its room), or every one (`all`). */
  readonly objects: 'all' | 'named';
}

/** What each kind's build reads (three/scene3d.ts `buildScene`). A read added
 *  to the build is added here, or an edit leaves a stale mesh — the unit cases
 *  build each relation in part and whole and compare (test/sceneChanges.test.mjs). */
export const SCENE_READS: Record<SceneKind, KindReads> = {
  // Its ends (an object, a text box, a floor), the drawn wall nearest its route
  // (a schematic one runs under its top), the roofs it passes over, its pipe type.
  conn: { fields: ['floorings', 'pipeTypes', 'texts'], roles: [], objects: 'all' },
  part: { fields: [], roles: [], objects: 'named' },
  polygon: { fields: [], roles: [], objects: 'named' },
  // The parts' holes through its walls, its mixers' risers, its fibre kits'
  // projectors (named), the beams its lights cast.
  room: { fields: ['lightBeams'], roles: ['inWall', 'risers'], objects: 'named' },
  // The parts' pockets in it.
  wall: { fields: [], roles: ['inWall'], objects: 'named' },
};

/** The drawing's fields built outside every object (the underlays): one moved,
 *  and the whole scene is built again. */
export const SCENE_WHOLE = ['background', 'backgroundImage', 'backgroundImages', 'backgroundOffset'] as const;

/** The fields some kind reads, each once. */
export const SCENE_FIELDS: readonly SceneField[] = [...new Set(Object.values(SCENE_READS).flatMap((r) => r.fields))];

/** What a scene was built from. */
export interface SceneBuilt {
  /** What every object is built with (`SCENE_WHOLE`'s versions, the catalogue,
   *  what is hidden). */
  readonly whole: string;
  /** The version of each field a kind reads (`SCENE_FIELDS`). */
  readonly fields: ReadonlyMap<SceneField, number>;
  /** Each object's version (-1: none of its own; -2: built live since). */
  readonly versions: ReadonlyMap<string, number>;
  /** The parts in each role as built: one that left its role is still read
   *  where it was. */
  readonly roles: Readonly<Record<PartRole, ReadonlySet<string>>>;
}

/** The parts in each role now. */
function rolesOf(objects: readonly SceneObject[]): Record<PartRole, Set<string>> {
  const out = Object.fromEntries((Object.keys(PART_ROLES) as PartRole[]).map((r) => [r, new Set<string>()])) as Record<PartRole, Set<string>>;
  for (const o of objects) {
    if (o.kind !== 'part') continue;
    for (const r of Object.keys(PART_ROLES) as PartRole[]) if (PART_ROLES[r](o.data as ProductInstance)) out[r].add(o.id);
  }
  return out;
}

/** The record of a scene built now from `objects`: `whole`, each object's
 *  version and each field's (`SCENE_FIELDS`), and who plays each role. */
export function sceneRecord(objects: readonly SceneObject[], whole: string, versionOf: (o: SceneObject) => number, fieldVersion: (f: SceneField) => number): SceneBuilt {
  return {
    whole,
    fields: new Map(SCENE_FIELDS.map((f) => [f, fieldVersion(f)])),
    versions: new Map(objects.map((o) => [o.id, versionOf(o)])),
    roles: rolesOf(objects),
  };
}

/** Every string an object holds, its own id aside, and what no build reads
 *  (`HEAVY_FIELDS`: a product's drawing, image, names, ports …) left out. */
function mentions(data: object, ids: ReadonlySet<string>): boolean {
  const walk = (v: unknown, top: boolean): boolean => {
    if (typeof v === 'string') return ids.has(v);
    if (!v || typeof v !== 'object') return false;
    if (Array.isArray(v)) return v.some((x) => walk(x, false));
    for (const k in v) if (!(top && k === 'id') && !(top && HEAVY_FIELDS.has(k)) && walk((v as Record<string, unknown>)[k], false)) return true;
    return false;
  };
  return walk(data, true);
}

/** THE rule: the objects to build again when `changed` changed — they, and
 *  every object that reads one of them by its kind's `SCENE_READS`: a kind
 *  reading a role, when a changed part plays it now or did as `built`; a kind
 *  reading all, on any change; an object whose data names one — until nothing
 *  more joins. */
export function sceneFollows(changed: Iterable<string>, objects: readonly SceneObject[], built: SceneBuilt | null): Set<string> {
  const ids = new Set(changed);
  if (!ids.size) return ids;
  const now = rolesOf(objects);
  for (let grew = true; grew;) {
    grew = false;
    const played = (Object.keys(PART_ROLES) as PartRole[]).filter((r) => [...ids].some((id) => now[r].has(id) || !!built?.roles[r].has(id)));
    for (const o of objects) {
      if (ids.has(o.id)) continue;
      const reads = SCENE_READS[o.kind];
      if (reads.objects === 'all' || reads.roles.some((r) => played.includes(r)) || mentions(o.data, ids)) { ids.add(o.id); grew = true; }
    }
  }
  return ids;
}

/** The ids to build again since `built` (null: the whole scene): what came,
 *  went or changed, every object of a kind whose field moved, and what follows
 *  (`sceneFollows`). */
export function sceneChanges(built: SceneBuilt | null, now: SceneBuilt, objects: readonly SceneObject[]): Set<string> | null {
  if (!built || built.whole !== now.whole) return null;
  for (const v of now.versions.values()) if (v < 0) return null;
  const ids = new Set<string>();
  for (const [id, v] of now.versions) if (built.versions.get(id) !== v) ids.add(id);
  for (const id of built.versions.keys()) if (!now.versions.has(id)) ids.add(id);
  const moved = SCENE_FIELDS.filter((f) => built.fields.get(f) !== now.fields.get(f));
  if (moved.length) for (const o of objects) if (SCENE_READS[o.kind].fields.some((f) => moved.includes(f))) ids.add(o.id);
  return sceneFollows(ids, objects, built);
}

/** The record after `ids` (a `sceneFollows` set) were built live: they are
 *  stale — the next refresh builds them again whatever their version (a cancel
 *  puts them back) — and the roles as the parts play them now count too, so
 *  one dragged into a wall and out again still takes its hole with it. */
export function markRebuilt(built: SceneBuilt, ids: Iterable<string>, objects: readonly SceneObject[]): SceneBuilt {
  const versions = new Map(built.versions);
  for (const id of ids) versions.set(id, -2);
  const now = rolesOf(objects);
  const roles = Object.fromEntries((Object.keys(PART_ROLES) as PartRole[]).map((r) => [r, new Set([...built.roles[r], ...now[r]])])) as Record<PartRole, Set<string>>;
  return { ...built, versions, roles };
}
